import { useQuery } from '@tanstack/react-query'
import { useId } from 'react'
import { Link } from 'react-router'

import { useAuth } from '../AuthContext'
import {
  PAST_CUTOFF_QUEUE_PATH,
  TRANSPORT_ATTENTION_KEY,
  fetchTransportAttention,
  isTransportDesk,
} from '../api/transportBookings'

/**
 * Paid transport bookings still without a driver past their cut-off (M40-T07,
 * spec §F.2, risk R6): the main way a customer gets let down, so it sits at
 * the top of the dashboard in red.
 *
 * Only for the transport desk's roles, which alone may ask. In practice that is
 * `amtc_admin`: `support` lands on emergency coordination and cannot open the
 * dashboard (`canOpen` in nav.ts); it sees these bookings as the red rows at
 * the top of the desk's queue (T12).
 * Nothing at 0, and
 * nothing if the count cannot be loaded: the queue itself still shows the red
 * rows, so a failed banner must not hide the rest of the dashboard.
 */
export function TransportAttentionBanner() {
  const { token, user } = useAuth()
  const allowed = isTransportDesk(user?.role)
  const headingId = useId()
  const attention = useQuery({
    queryKey: TRANSPORT_ATTENTION_KEY,
    queryFn: () => fetchTransportAttention(token),
    enabled: Boolean(token) && allowed,
    // A dashboard left open must not show a stale count until refocused.
    refetchInterval: 60_000,
  })

  const count = attention.data?.past_cutoff ?? 0
  if (!allowed || count === 0) return null

  const sentence =
    count === 1
      ? '1 transport booking has no driver and is past the cut-off'
      : `${count} transport bookings have no driver and are past the cut-off`
  return (
    // A standing count, not an event: a named region, not an alert, so it is
    // not announced over whatever the reader is doing on every visit or
    // refetch. Named by its own sentence, so the number is in the name.
    <section
      aria-labelledby={headingId}
      className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-bed-bad/40 bg-bed-bad-bg px-5 py-4"
    >
      <p id={headingId} className="text-sm font-semibold text-bed-bad">
        {sentence}
      </p>
      <Link
        to={PAST_CUTOFF_QUEUE_PATH}
        className="inline-flex h-9 items-center rounded-[8px] border border-bed-bad/40 bg-bg-app px-3 text-sm font-semibold text-bed-bad hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        Open the transport queue
      </Link>
    </section>
  )
}
